import React from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CustomizationSection } from 'components/SmartAssistantSections/CustomizationSection/CustomizationSection';
import { useCommonCustomizationForm } from 'hooks/SmartAssistantForms/use-common-customization-form';
import { useMultiUpdateNotificationHandling } from 'hooks/use-multi-update-notification-handling';
import { ManageSmartAssistantSectionControlWrapper } from 'pages/ManageSmartAssistant/ManageSmartAssistantControlWrapper';
import { useTranslation } from 'react-i18next';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { SMART_ASSISTANT_QUERY_KEY } from 'shared/constants/query-keys';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import { formatHexColor } from 'shared/utils/format';

interface CustomizationManagementSectionProps {
  smartAssistant: SmartAssistant;
}

export function CustomizationManagementSection({ smartAssistant }: CustomizationManagementSectionProps) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const customizationForm = useCommonCustomizationForm(smartAssistant.type, smartAssistant);
  const { showUpdateNotification, resetNotificationStatusTracking } = useMultiUpdateNotificationHandling({
    feedbackKey: 'UPDATE_CUSTOMIZATION'
  });

  const saveMutation = useMutation({
    mutationFn: SmartAssistantService.updateSmartAssistant,
    onSuccess: (updatedSmartAssistant: SmartAssistant) => {
      queryClient.setQueryData([SMART_ASSISTANT_QUERY_KEY, { smartAssistantId: smartAssistant._id }], () => {
        return updatedSmartAssistant;
      });
      customizationForm.resetDirty();
      showUpdateNotification(true);
    },
    onError: () => showUpdateNotification(false)
  });

  const onSave = async () => {
    resetNotificationStatusTracking();
    const validation = customizationForm.validate();
    if (validation.hasErrors) return;

    saveMutation.mutate({
      id: smartAssistant._id,
      ...customizationForm.values,
      assistantMessageColor: formatHexColor(customizationForm.values.assistantMessageColor),
      userMessageColor: formatHexColor(customizationForm.values.userMessageColor),
      conversationBackgroundColor: formatHexColor(customizationForm.values.conversationBackgroundColor),
      secondaryBackgroundColor: formatHexColor(customizationForm.values.secondaryBackgroundColor),
      gradientColors: (customizationForm.values.gradientColors ?? []).map((color) => formatHexColor(color) ?? '')
    });
  };

  const onCancel = () => {
    customizationForm.reset();
    resetNotificationStatusTracking();
  };

  const hasChanges = customizationForm.isDirty();

  return (
    <ManageSmartAssistantSectionControlWrapper
      title={t('SMART_ASSISTANT.CUSTOMIZATION_SECTION.TITLE')}
      hasChanges={hasChanges}
      loading={saveMutation.isLoading}
      onSave={onSave}
      onCancel={onCancel}>
      <CustomizationSection form={customizationForm} smartAssistant={smartAssistant} />
    </ManageSmartAssistantSectionControlWrapper>
  );
}
